import { Component, computed, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoPipe } from '@jsverse/transloco';
import { startWith } from 'rxjs';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { PositionShare } from '../../../../api/app-types/user.app';
import { CustomDateAdapter } from '../../../../core/adapters/custom-date-adapter';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { formatDateToISOString, isValidDate } from '../../../../shared/helpers/date.func';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { dateRangeValidator } from '../../../../shared/validators/date-range-validator';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { hasPermission } from '../../../../shared/permissions/permission.func';

@Component({
	selector: 'lvr-position-share-edit',
	imports: [
		LvrPaddingDirective,
		TranslocoPipe,
		LvrAppendAsteriskPipe,
		LvrField,
		LvrInputDirective,
		ReactiveFormsModule,
		MatInputModule,
		MatDatepickerModule,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrDefaultActions,
	],
	templateUrl: './position-share-edit.html',
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { subscriptSizing: 'dynamic' },
		},
		{ provide: DateAdapter, useClass: CustomDateAdapter },
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
		{ provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
	],
})
export class PositionShareEdit implements OnInit {
	private readonly env = inject(ENVIRONMENT);
	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);

	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly gracePeriodYears = this.env.gracePeriodYears;
	public readonly canEditPositionSharesPermission = hasPermission(
		this.session.permissions,
		'Users.PositionShares.Edit',
	);
	public readonly hasError = hasError;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly draftId = computed(() => {
		return `${FORM_KEYS.UPDATE_POSITION_SHARE}.${this.positionShare()?.id}`;
	});
	public readonly maxStartDate = new Date(new Date().setFullYear(new Date().getFullYear() + this.gracePeriodYears));
	public positionShare = input<PositionShare>();

	public readonly shareSaved = output<PositionShare>();
	public readonly shareCancel = output<void>();

	public readonly form = this.initForm();
	private readonly startCtrl =
		this.form.get('start') ??
		(() => {
			throw new Error('Missing control: start');
		})();
	private readonly startSig = toSignal(this.startCtrl.valueChanges.pipe(startWith(this.startCtrl.value)), {
		initialValue: this.startCtrl.value,
	});

	public readonly minEndDate = computed(() => {
		const value = this.startSig();
		return value ? new Date(value) : null;
	});

	public ngOnInit() {
		this.initializeFormFromShare();
	}

	public handleCancelEdit() {
		this.draftService.deleteDraft('user', this.draftId());
		this.shareCancel.emit();
	}

	public submit() {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		const currentShare = this.positionShare();
		if (!currentShare) return;

		const formValue = this.form.value;

		const data: PositionShare = {
			id: currentShare.id,
			share: formValue.share,
			start: formatDateToISOString(formValue.start) ?? '',
			end: formatDateToISOString(formValue.end),
		};

		this.shareSaved.emit(data);
	}

	private initializeFormFromShare() {
		const share = this.positionShare();
		this.form.patchValue({
			...share,
			end: isValidDate(share?.end) ? share?.end : '',
		});
	}

	private initForm(): FormGroup {
		const fb = inject(FormBuilder);
		const formGroup = fb.group({
			share: [undefined, [Validators.required, Validators.min(0), Validators.max(1)]],
			start: [undefined, [Validators.required, this.gracePeriodValidator()]],
			end: [undefined],
		});

		this.dateRangeValidator(formGroup);

		return formGroup;
	}

	/**
	 * Ensures the selected `end` date comes after the `start` date.
	 * The validation updates on value change.
	 */
	private dateRangeValidator(formGroup: FormGroup) {
		const startCtrl = formGroup.get('start');
		const endCtrl = formGroup.get('end');

		const applyValidator = () => {
			endCtrl?.setValidators([dateRangeValidator(startCtrl, endCtrl)]);
			endCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		};

		endCtrl?.valueChanges.pipe(startWith(endCtrl.value), takeUntilDestroyed()).subscribe(() => {
			applyValidator();
		});

		startCtrl?.valueChanges.pipe(startWith(startCtrl.value), takeUntilDestroyed()).subscribe(() => {
			applyValidator();
		});

		applyValidator();
	}

	/**
	 * Checks that the selected date does not exceed the allowed grace period.
	 */
	private gracePeriodValidator(): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control.value) return null;
			const selected = new Date(control.value);
			const maxDate = new Date();
			maxDate.setFullYear(maxDate.getFullYear() + this.gracePeriodYears);
			return selected > maxDate ? { gracePeriod: true } : null;
		};
	}
}
